import { useGameState } from "../game";
import { createLiveFocusTracker, LIVE_FOCUS_STALE_MS } from "../live-focus";
import type { Actor } from "../overwolf/plugin";
import { rememberPalCaptureCounts } from "../pal-capture";
import { useSettingsStore } from "../settings";
import { RunningGame } from "./games";
import { legacyIdsOutsideSets } from "../game-reported-nodes";
import { actorsChanged } from "./actors-changed";
import { gatherMapDebugState } from "./map-debug-state";
import { useLiveState, useTHGLAppState } from "./states";
import {
  compatRunAsAdminFlagState,
  getInitialStateFromWebview,
} from "./version";
import { postWebviewMessage } from "./webview";

export type WindowMode = "overlay" | "desktop" | "both";

export function openDashboadWebView() {
  return postWebviewMessage({
    action: "openDashboardWebView",
    payload: {},
  });
}
export function openOverlayWebView(url: string, title: string) {
  let fullUrl = url;
  if (!fullUrl.startsWith("http")) {
    fullUrl = location.origin + fullUrl;
  }
  return postWebviewMessage({
    action: "openOverlayWebView",
    payload: {
      url: fullUrl,
      title,
    },
  });
}
// From a game's own window the app shows that game's overlay. Windows of no game
// (the dashboard) pass the game's overlay URL, else the app picks the first
// running game (older apps ignore the URL).
export function showOverlay(url?: string) {
  return postWebviewMessage({
    action: "showOverlay",
    payload: url ? { url } : {},
  });
}
export function hideOverlay() {
  return postWebviewMessage({
    action: "hideOverlay",
    payload: {},
  });
}
export function updateHotkeys(hotkeys: Record<string, string>) {
  return postWebviewMessage({
    action: "updateHotkeys",
    payload: hotkeys,
  });
}

export function updateHotkeyBlocking(blocked: string[]) {
  return postWebviewMessage({
    action: "updateHotkeyBlocking",
    payload: { blocked },
  });
}

// Sync current hotkeys from settings store to C++
function syncHotkeysToNative() {
  const { hotkeys, hotkeyPassthrough } = useSettingsStore.getState();
  // Filter out empty values but keep the action->combo mapping
  const filteredHotkeys: Record<string, string> = {};
  for (const [action, combo] of Object.entries(hotkeys)) {
    if (combo) {
      filteredHotkeys[action] = combo;
    }
  }
  if (Object.keys(filteredHotkeys).length > 0) {
    console.log("Syncing hotkeys to native:", filteredHotkeys);
    updateHotkeys(filteredHotkeys).catch(console.error);
  }
  // Every hotkey not set to "Send key to game" is swallowed in the game.
  // Apps before 20.1.0 don't know the action and keep passing every key.
  const blocked = Object.keys(hotkeys).filter(
    (action) => !hotkeyPassthrough?.[action],
  );
  updateHotkeyBlocking(blocked).catch(() => {});
}

export function updateActorTypeFilters(types: string[], processName?: string) {
  return postWebviewMessage({
    action: "setActorTypeFilter",
    payload: {
      types,
      processName,
    },
  });
}

/**
 * Posts a debug snapshot to the THGL team. `discordUsername` is the reporter's
 * Discord handle: snapshots are only useful next to a support conversation, and
 * without a way back to the reporter the post is unanswerable (the app-debug
 * channel filled up with anonymous "chest" / "treasure map" contexts).
 */
export async function sendDebugSnapshot(
  userContext: string,
  discordUsername: string,
) {
  // The map side (live mode, overrides, ticked filters) rides along: the
  // detector side alone can't explain "live markers invisible" (inbox #902).
  const mapState = await gatherMapDebugState().catch((error) => {
    console.error("Failed to gather map debug state:", error);
    return null;
  });
  return postWebviewMessage({
    action: "sendDebugSnapshot",
    payload: {
      userContext,
      discordUsername,
      mapState,
    },
  });
}

export function openInBrowser(url: string) {
  return postWebviewMessage({
    action: "openInBrowser",
    payload: {
      url,
    },
  });
}

export function openDesktopWebView(url: string, title: string) {
  let fullUrl = url;
  if (!fullUrl.startsWith("http")) {
    fullUrl = location.origin + fullUrl;
  }
  return postWebviewMessage({
    action: "openDesktopWebView",
    payload: {
      url: fullUrl,
      title,
    },
  });
}

export function openDevTools() {
  window.chrome.webview.postMessage("openDevTools");
}

export function openDevToolsForUrl(url: string) {
  return postWebviewMessage({
    action: "openDevTools",
    payload: { url },
  });
}

export function closeWebViews(urls: string[]) {
  return postWebviewMessage({
    action: "closeWebViews",
    payload: { urls },
  });
}

export function getVersion() {
  return postWebviewMessage<{
    buildDate: string;
    buildTime: string;
    buildVersion: string;
  }>({
    action: "getVersion",
    payload: {},
  });
}

export function getIsTaskInstalled() {
  return postWebviewMessage<boolean>({
    action: "isTaskInstalled",
    payload: {},
  });
}

export function addScheduledTask() {
  return postWebviewMessage({
    action: "addScheduledTask",
    payload: {},
  });
}

export function removeScheduledTask() {
  return postWebviewMessage({
    action: "removeScheduledTask",
    payload: {},
  });
}

export function getWindowMode() {
  return postWebviewMessage<WindowMode>({
    action: "getWindowMode",
    payload: {},
  });
}

export function setWindowMode(mode: WindowMode) {
  return postWebviewMessage({
    action: "setWindowMode",
    payload: {
      mode,
    },
  });
}

let initialized = false;
let prevPlayer: {
  x: number;
  y: number;
  z: number;
  r: number | null;
  mapName?: string;
  terraformStage?: string;
  camera?: boolean;
  party?: unknown[];
} | null = null;
let prevActors: {
  address: number;
  x: number;
  y: number;
  z: number;
  hidden?: boolean;
}[] = [];
let prevStaticActors: {
  address: number;
  x: number;
  y: number;
  z: number;
  hidden?: boolean;
}[] = [];
// The native side re-broadcasts the dungeon floor plan on a slow cadence (to beat
// webview load races), so skip an identical resend — mapName + triangle count is a
// sufficient signature since the buffer is read once per dungeon entry.
let prevNavmesh: { mapName: string; triangles: number } | null = null;
let lastActorUpdateTime = 0;
// Trailing-edge throttle state: the native side broadcasts the "actors" payload
// only when it CHANGES (edge-triggered). A leading-edge-only throttle would drop
// a change that lands inside the window and never see it again (the native side
// won't re-send an unchanged payload), leaving the map stuck on the previous set
// — e.g. select NONE then Pickups and the pickups never appear. Remember the last
// throttled payload and flush it when the window elapses so no edge is ever lost.
let pendingActors: Actor[] | null = null;
let pendingActorTimer: ReturnType<typeof setTimeout> | null = null;
// Matches the native actor poll (100 ms). The old 200 ms throttle protected
// React from re-processing huge combined actor arrays; since the immobile
// subset moved to the change-gated "staticActors" channel, the per-poll
// "actors" payload is movers-only and small, so no extra delay is needed.
const ACTOR_THROTTLE_MS = 100;

export async function initializeApp(role: "client" | "dashboard" = "client") {
  if (initialized) {
    return;
  }
  initialized = true;

  const gameState = useGameState.getState();
  const liveState = useLiveState.getState();
  const liveFocus = createLiveFocusTracker(
    gameState.setHighlightSpawnIDs,
    LIVE_FOCUS_STALE_MS,
    gameState.setLiveFocusActive,
  );

  // Listen for direct WebView messages from C++
  if (typeof window !== "undefined" && window.chrome?.webview) {
    window.chrome.webview.addEventListener("message", (event: MessageEvent) => {
      try {
        const message =
          typeof event.data === "string" ? JSON.parse(event.data) : event.data;
        if (typeof message === "object" && typeof message.action === "string") {
          // Client (Overlay/Desktop) specific handlers
          if (role === "client") {
            if (message.action === "player") {
              const p = message.payload;
              if (
                !prevPlayer ||
                p.x !== prevPlayer.x ||
                p.y !== prevPlayer.y ||
                p.z !== prevPlayer.z ||
                p.r !== prevPlayer.r ||
                p.mapName !== prevPlayer.mapName ||
                p.terraformStage !== prevPlayer.terraformStage ||
                p.camera !== prevPlayer.camera ||
                // Party games: the camera can stand still while the party walks.
                JSON.stringify(p.party) !== JSON.stringify(prevPlayer.party)
              ) {
                prevPlayer = p;
                gameState.setPlayer({
                  ...p,
                  address: 0,
                  type: "player",
                });
              }
            } else if (message.action === "actors") {
              const now = performance.now();
              const actors = message.payload;
              if (actorsChanged(prevActors, actors)) {
                const elapsed = now - lastActorUpdateTime;
                if (elapsed >= ACTOR_THROTTLE_MS) {
                  // Leading edge: apply immediately and cancel any pending flush.
                  if (pendingActorTimer !== null) {
                    clearTimeout(pendingActorTimer);
                    pendingActorTimer = null;
                    pendingActors = null;
                  }
                  prevActors = actors;
                  lastActorUpdateTime = now;
                  gameState.setActors(actors);
                } else {
                  // Inside the throttle window: keep the latest payload and flush it
                  // when the window elapses, so an edge-triggered change is never lost.
                  pendingActors = actors;
                  if (pendingActorTimer === null) {
                    pendingActorTimer = setTimeout(() => {
                      pendingActorTimer = null;
                      const flush = pendingActors;
                      pendingActors = null;
                      if (flush && actorsChanged(prevActors, flush)) {
                        prevActors = flush;
                        lastActorUpdateTime = performance.now();
                        gameState.setActors(flush);
                      }
                    }, ACTOR_THROTTLE_MS - elapsed);
                  }
                }
              }
            } else if (message.action === "actorsDelta") {
              // Incremental mover update: only the actors that moved/appeared plus the
              // addresses that disappeared, instead of the full per-poll list. Applied
              // immediately — deltas arrive at most at the backend poll rate (~10/s) and
              // are small, so no throttle is needed (unlike the full-payload keyframe).
              const { changed, removed } = message.payload;
              // A full "actors" keyframe is still the source of truth for the throttle
              // gate; a delta doesn't reconstruct prevActors, so invalidate it so the
              // next keyframe isn't wrongly skipped as "unchanged".
              prevActors = [];
              gameState.applyActorsDelta(changed ?? [], removed ?? []);
            } else if (message.action === "staticActorsDelta") {
              // Incremental update: a harvest is one removed address, a respawn
              // wave a few added actors — no full-payload parse or re-render.
              const { added, removed } = message.payload;
              prevStaticActors = []; // full-payload guard no longer representative
              gameState.applyStaticActorsDelta(added ?? [], removed ?? []);
            } else if (message.action === "staticActors") {
              // Fixed-position actors (foliage resource nodes etc.) — sent on
              // change plus a periodic catch-up re-send; skip identical payloads
              // so the re-send doesn't trigger pointless re-renders.
              const staticActors = message.payload;
              if (actorsChanged(prevStaticActors, staticActors)) {
                prevStaticActors = staticActors;
                gameState.setStaticActors(staticActors);
              }
            } else if (message.action === "characterData") {
              // Markers to focus (AION 2: open quest objectives), see live-focus.ts.
              liveFocus.apply(message.payload);
              gameState.setCharacter(message.payload);
              rememberPalCaptureCounts(message.payload);
              // Named sets of collected nodes, each the COMPLETE list for the
              // character being played (AION 2 "empyrean_traces", Aniimo's weekly
              // "voxel_coins"): a present key replaces that set, so the marks
              // follow the character / week; an absent key = unknown, that set is
              // left alone. When sets are sent, the legacy collectedNodeIds entries
              // that a set mirrors (still sent for old frontends) are ignored.
              const collectedSets = message.payload?.collectedNodeSets;
              const hasCollectedSets =
                typeof collectedSets === "object" &&
                collectedSets !== null &&
                !Array.isArray(collectedSets);
              if (hasCollectedSets) {
                const settings = useSettingsStore.getState();
                if (settings.autoDiscoverCollected) {
                  for (const [setName, ids] of Object.entries(collectedSets)) {
                    if (
                      Array.isArray(ids) &&
                      ids.every((id) => typeof id === "string")
                    ) {
                      settings.applyGameReportedSet(setName, ids);
                    }
                  }
                }
              }
              // Nodes the game itself reports as collected (e.g. Aniimo's server-synced
              // map-mark status: opened chests / picked-up Lumin Amber, account-wide).
              // Plain spawn ids — discovery matches a node's base id — so they apply in
              // every live mode and to pins that are not loaded or filtered out.
              const collectedRaw = message.payload?.collectedNodeIds;
              const collected =
                hasCollectedSets && Array.isArray(collectedRaw)
                  ? legacyIdsOutsideSets(collectedRaw, collectedSets)
                  : collectedRaw;
              if (Array.isArray(collected) && collected.length > 0) {
                const settings = useSettingsStore.getState();
                if (settings.autoDiscoverCollected) {
                  const known = new Set(settings.discoveredNodes);
                  // Aniimo reports every collected map mark as "e<markId>", but pins
                  // drawn from the game's map marks (puzzles, Lumin Collection, …)
                  // carry the id "m<markId>" — mark both forms.
                  const ids = collected.flatMap((id) =>
                    typeof id === "string" && /^e\d+$/.test(id)
                      ? [id, `m${id.slice(1)}`]
                      : [id],
                  );
                  const fresh = ids.filter(
                    (id): id is string =>
                      typeof id === "string" && !known.has(id),
                  );
                  if (fresh.length > 0) settings.markAutoDiscovered(fresh);
                }
              }
            } else if (message.action === "dungeonNavmesh") {
              // Floor plan for the dungeon the player is in, re-sent on a slow cadence.
              // Skip identical resends; apply only when the dungeon (or its triangle
              // count) changes. The NavmeshLayer scopes it to payload.mapName and clears
              // when the player leaves that map (see interactive-map / LiveNavmesh).
              const nm = message.payload;
              if (
                !prevNavmesh ||
                prevNavmesh.mapName !== nm.mapName ||
                prevNavmesh.triangles !== nm.triangles
              ) {
                prevNavmesh = { mapName: nm.mapName, triangles: nm.triangles };
                gameState.setDungeonNavmesh(nm);
              }
            } else if (message.action === "windowModeChanged") {
              liveState.setWindowMode(message.payload);
            } else if (message.action === "alwaysRunAsAdminChanged") {
              liveState.setAlwaysRunAsAdmin(message.payload);
            } else if (message.action === "helpImproveMapsChanged") {
              liveState.setHelpImproveMaps(message.payload);
            } else if (message.action === "exclusiveFullscreenChanged") {
              liveState.setExclusiveFullscreen(message.payload);
            }
          }
          // Dashboard specific handlers - receive directly from C++
          if (role === "dashboard") {
            if (message.action === "driverHealth") {
              liveState.setDriverHealth(message.payload);
            } else if (message.action === "runningGames") {
              liveState.setRunningGames(message.payload);
              // Cleanup stale sessions - mark sessions as closed if their PID is no longer active
              const activePids = message.payload.map((g: RunningGame) => g.pid);
              useTHGLAppState.getState().cleanupStaleSessions(activePids);
            } else if (message.action === "gameSession") {
              useTHGLAppState.getState().updateGameSession(message.payload);
            } else if (message.action === "connectedClients") {
              liveState.setConnectedClients(message.payload);
            } else if (message.action === "version") {
              liveState.setVersion(message.payload);
            } else if (message.action === "isTaskInstalled") {
              liveState.setIsTaskInstalled(message.payload);
            } else if (message.action === "windowModeChanged") {
              liveState.setWindowMode(message.payload);
            } else if (message.action === "alwaysRunAsAdminChanged") {
              liveState.setAlwaysRunAsAdmin(message.payload);
            } else if (message.action === "helpImproveMapsChanged") {
              liveState.setHelpImproveMaps(message.payload);
            } else if (message.action === "closeActionChanged") {
              liveState.setCloseAction(message.payload);
            } else if (message.action === "exclusiveFullscreenChanged") {
              liveState.setExclusiveFullscreen(message.payload);
            }
          }
          // Client (Overlay/Desktop) handlers for DevTools and close requests
          if (role === "client") {
            if (message.action === "openDevTools") {
              if (message.payload.url === location.href) {
                openDevTools();
              }
            } else if (message.action === "closeWebViews") {
              if (message.payload.includes(location.href)) {
                postWebviewMessage({
                  action: "clickthroughOverlayWebView",
                  payload: {
                    clickthrough: true,
                  },
                }).catch(() => {});
                window.chrome.webview.postMessage("close");
              }
            }
          }
        }
      } catch (e) {
        // Ignore parse errors for non-JSON messages
      }
    });
    console.log("Direct WebView message listener registered for", role);
  }

  // For dashboard role, request initial state from C++ when ready
  if (role === "dashboard") {
    const fetchInitialState = async (
      retries = 3,
      delay = 500,
    ): Promise<void> => {
      for (let attempt = 1; attempt <= retries; attempt++) {
        try {
          const res = await getInitialStateFromWebview();
          const data = res.data;
          liveState.setVersion(data.version);
          // isTaskInstalled is fetched separately to avoid blocking
          if (data.isTaskInstalled !== null) {
            liveState.setIsTaskInstalled(data.isTaskInstalled);
          }
          liveState.setWindowMode(data.windowMode);
          liveState.setGpuFlag(data.gpuFlag);
          liveState.setIsRunningAsAdmin(data.isRunningAsAdmin ?? false);
          liveState.setAlwaysRunAsAdmin(data.alwaysRunAsAdmin ?? false);
          liveState.setHelpImproveMaps(data.helpImproveMaps ?? true);
          liveState.setCompatRunAsAdminFlag(compatRunAsAdminFlagState(data));
          liveState.setExclusiveFullscreen(data.exclusiveFullscreen ?? false);
          liveState.setCloseAction(data.closeAction ?? "ask");
          liveState.setLocale(data.locale ?? "en");
          liveState.setDiscordPresence(data.discordPresence ?? null);
          if (data.connectedClients) {
            liveState.setConnectedClients(data.connectedClients);
          }
          if (data.driverHealth) {
            liveState.setDriverHealth(data.driverHealth);
          }
          if (data.compatRunAsAdminFlagRemoved) {
            // Surfaced as a toast by CompatFlagNotice (ui layer) — this
            // package can't import sonner directly.
            window.dispatchEvent(
              new CustomEvent("thgl-app:compat-flag-removed"),
            );
          }
          console.log("Dashboard received initial state:", data);

          // Cleanup stale sessions on startup - use current running games if available, else empty
          const currentRunningGames = liveState.runningGames ?? [];
          const activePids = currentRunningGames.map((g) => g.pid);
          useTHGLAppState.getState().cleanupStaleSessions(activePids);
          return; // Success, exit
        } catch (e) {
          console.warn(
            `Failed to get initial state (attempt ${attempt}/${retries}):`,
            e,
          );
          if (attempt < retries) {
            await new Promise((resolve) => setTimeout(resolve, delay));
          }
        }
      }
      console.error("Failed to get initial state after all retries");
    };
    fetchInitialState();
  }

  // For client role, fetch window mode and close action from C++
  if (role === "client") {
    getWindowMode()
      .then((res) => {
        liveState.setWindowMode(res.data);
      })
      .catch(console.error);

    getInitialStateFromWebview()
      .then((res) => {
        liveState.setCloseAction(res.data.closeAction ?? "ask");
        liveState.setExclusiveFullscreen(res.data.exclusiveFullscreen ?? false);
      })
      .catch(console.error);

    // Sync hotkeys to C++ after store hydration and on changes.
    // Uses a delayed confirmation re-sync to handle WebView2 IPC timing.
    let prevHotkeys = useSettingsStore.getState().hotkeys;
    let prevPassthrough = useSettingsStore.getState().hotkeyPassthrough;
    let hasSynced = false;
    let confirmTimer: ReturnType<typeof setTimeout> | null = null;
    const syncWithConfirm = () => {
      syncHotkeysToNative();
      if (confirmTimer) clearTimeout(confirmTimer);
      confirmTimer = setTimeout(syncHotkeysToNative, 300);
    };
    const trySyncInitial = () => {
      if (!hasSynced && useSettingsStore.getState()._hasHydrated) {
        hasSynced = true;
        prevHotkeys = useSettingsStore.getState().hotkeys;
        prevPassthrough = useSettingsStore.getState().hotkeyPassthrough;
        syncWithConfirm();
      }
    };
    trySyncInitial();
    useSettingsStore.subscribe((state) => {
      trySyncInitial();
      if (
        hasSynced &&
        (state.hotkeys !== prevHotkeys ||
          state.hotkeyPassthrough !== prevPassthrough)
      ) {
        prevHotkeys = state.hotkeys;
        prevPassthrough = state.hotkeyPassthrough;
        syncWithConfirm();
      }
    });
  }
}
